import BaseAPI from 'utils/api/base';

import { APIRequest } from 'utils/api/types';

import {
  AuditTaskListResponse,
  CreateScheduleData,
  PreviewAssigneeGroupsData,
  PreviewAssigneeGroupsResponse,
  PreviewCycleResponse,
  PreviewCyclesData,
  ScheduleDetailsResponse,
  ScheduleInstanceResponse,
  UpdateScheduleData,
  UpdateScheduleInstanceData,
} from './schedules.types';

export interface LoadAuditTasksParams {
  dueDate: string;
  assignedTo?: string;
  status?: string;
  startDate?: string;
  // Opt out of the backend's overdue-outside-window union, so counts stay strictly in range.
  strictDueDate?: boolean;
  ordering?: string;
  limit?: number;
  offset?: number;
}

class SchedulesAPI extends BaseAPI {
  loadAuditTasks = (params: LoadAuditTasksParams): APIRequest<AuditTaskListResponse> => {
    const url = '/schedules/tasks/';
    const method = 'GET';

    return this.request<AuditTaskListResponse>({
      url,
      method,
      params: {
        type: 'audit',
        withPagination: true,
        ...params,
      },
    });
  };

  loadScheduleDetails = (scheduleId: string): APIRequest<ScheduleDetailsResponse> => {
    const url = `/schedules/${scheduleId}/`;
    const method = 'GET';

    return this.request<ScheduleDetailsResponse>({
      url,
      method,
    });
  };

  createSchedule = (data: CreateScheduleData): APIRequest<ScheduleDetailsResponse> => {
    const url = '/schedules/';
    const method = 'POST';

    return this.request<ScheduleDetailsResponse>({
      url,
      method,
      data,
    });
  };

  updateSchedule = (scheduleId: string, data: UpdateScheduleData): APIRequest<ScheduleDetailsResponse> => {
    const url = `/schedules/${scheduleId}/`;
    const method = 'PATCH';

    return this.request<ScheduleDetailsResponse>({
      url,
      method,
      data,
    });
  };

  deleteSchedule = (scheduleId: string): APIRequest<void> => {
    const url = `/schedules/${scheduleId}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  pauseSchedule = (scheduleId: string): APIRequest<ScheduleDetailsResponse> => {
    const url = `/schedules/${scheduleId}/pause/`;
    const method = 'POST';

    return this.request<ScheduleDetailsResponse>({
      url,
      method,
    });
  };

  resumeSchedule = (scheduleId: string): APIRequest<ScheduleDetailsResponse> => {
    const url = `/schedules/${scheduleId}/resume/`;
    const method = 'POST';

    return this.request<ScheduleDetailsResponse>({
      url,
      method,
    });
  };

  loadScheduleInstance = (scheduleId: string, instanceId: string): APIRequest<ScheduleInstanceResponse> => {
    const url = `/schedules/${scheduleId}/instances/${instanceId}/`;
    const method = 'GET';

    return this.request<ScheduleInstanceResponse>({
      url,
      method,
    });
  };

  startAuditFromSchedule = (scheduleId: string, instanceId: string): APIRequest<ScheduleInstanceResponse> => {
    const url = `/schedules/${scheduleId}/instances/${instanceId}/start/`;
    const method = 'POST';

    return this.request<ScheduleInstanceResponse>({
      url,
      method,
    });
  };

  updateScheduleInstance = (
    scheduleId: string,
    instanceId: string,
    data: UpdateScheduleInstanceData
  ): APIRequest<ScheduleInstanceResponse> => {
    const url = `/schedules/${scheduleId}/instances/${instanceId}/`;
    const method = 'PATCH';

    return this.request<ScheduleInstanceResponse>({
      url,
      method,
      data,
    });
  };

  previewAssigneeGroups = (data: PreviewAssigneeGroupsData): APIRequest<PreviewAssigneeGroupsResponse> => {
    const url = '/schedules/preview-assignee-groups/';
    const method = 'POST';

    return this.request<PreviewAssigneeGroupsResponse>({
      url,
      method,
      data,
    });
  };

  previewCycles = (data: PreviewCyclesData): APIRequest<PreviewCycleResponse[]> => {
    const url = '/schedules/preview-cycles/';
    const method = 'POST';

    return this.request<PreviewCycleResponse[]>({
      url,
      method,
      data,
    });
  };
}

export default SchedulesAPI;
